import React from "react";
import {displayFunctions} from "../StoreTotals/Components/StoreTotalsDataSelections";
import {getHeader} from "./StoreTotalsHeaderComponent";
import {sortAscendingWithNullsFirst} from "../../../utils/sorting";
import {getWatermarkForRetailer} from "./getWatermarkForRetailer";
import StoreTotalsCell from "../StoreTotals/Components/StoreTotalsCell";
import DebouncedColumnFilter from "../../Components/ReactTable/DebouncedColumnFilter";


export const detailedSummaryColumns = [
    {
        header: "Store Name",
        accessorKey: "storeName",
        sticky: 'left',
        className: "total-dollar-store-name",
        headerClassName: "total-dollar-store-name",
        minSize: 350,
        filter: DebouncedColumnFilter,
        headerStyle: {flex: '2 0 auto'},
        cellStyle: {flex: '2 0 auto'},
        footer: <span>Totals:</span>,
    },
    {
        header: "Store ID",
        accessorKey: "storeId",
        className: "total-dollar-store-id",
        sticky: 'left',
        size: 100,
        filter: DebouncedColumnFilter,
        filterFn: (row, columnIds, filterValue) => !filterValue || Boolean(`${row.original.storeId}`.startsWith(filterValue)),
    }
];

export const getStoreTotalsDataColumns = ({
                                              retailerId,
                                              model,
                                              outlierIndex,
                                              contextMenuId,
                                              selectedData,
                                              setTimestamp,
                                              cellValueClicked,
                                              fillWatermark,
                                              promoWatermark
                                          }) => {
    const dataSelection = displayFunctions[selectedData];
    const getOutlier = (store, date) => {
        const cleansed = store.cells.get(date)?.cleansed;
        return cleansed ? outlierIndex.outlierFor(store.storeId, cleansed) : undefined;
    };
    const averageColumn = {
        header: "Average",
        id: "Average",
        className: "total-dollar-average",
        size: 100,
        sticky: 'left',
        enableColumnFilter: false,
        enableColumnSort: true,
        accessorFn: (store) => {
            const average = outlierIndex.averageFor(store.storeId);
            return average?.[dataSelection.averageKey] ?? null;
        },
        cell: ({row}) => {
            const average = outlierIndex.averageFor(row.original.storeId);
            return average ? `${dataSelection.displayAverageFunction(average)}` : '';
        },
        sortingFn: (rowA, rowB, columnId) => sortAscendingWithNullsFirst(rowA.getValue(columnId), rowB.getValue(columnId))
    };

    const retailerFillWatermark = getWatermarkForRetailer(fillWatermark, retailerId, "dateFilled");
    const retailerPromoWatermark = getWatermarkForRetailer(promoWatermark, retailerId, "datePromo");
    const millisecondsInDays = 1000 * 60 * 60 * 24;
    const dateColumns = model.columnDates.map((date) => {
        const headerClassName = model.expectedDates.includes(date) ? "totals-header" : "totals-header-emphasis";
        return {
            id: date,
            footer: () => {
                const total = model.footerTotals.get(date) || 0;
                return <span>{dataSelection.formatTotal(total)}</span>;
            },
            header: getHeader(retailerId, date, model.storeIds, setTimestamp, headerClassName,
                (new Date(date) - new Date(retailerFillWatermark)) < (7 * millisecondsInDays) && (new Date(date) - new Date(retailerFillWatermark)) >= 0 ? retailerFillWatermark : undefined,
                (new Date(date) - new Date(retailerPromoWatermark)) < (7 * millisecondsInDays) && (new Date(date) - new Date(retailerPromoWatermark)) >= 0 ? retailerPromoWatermark : undefined),
            size: 120,
            accessorFn: (store) => {
                const outlier = getOutlier(store, date);
                return outlier?.[dataSelection.diffKey] ?? null;
            },
            cell: ({row, table}) => {
                const {configOverlay, displayDataFunction, styleForOutlierFunction} = table.options.meta;
                const store = row.original;
                const outlier = getOutlier(store, date);
                return <StoreTotalsCell store={store}
                                        date={date}
                                        cell={store.cells.get(date)}
                                        flags={configOverlay.flagsByStore.get(store.storeId)?.get(date)}
                                        closures={configOverlay.closuresByStore.get(store.storeId)}
                                        outlier={outlier}
                                        displayDataFunction={displayDataFunction}
                                        outlierClassName={styleForOutlierFunction(outlier)}
                                        retailerId={retailerId}
                                        contextMenuId={contextMenuId}
                                        setTimestamp={setTimestamp}
                                        cellValueClicked={cellValueClicked}/>;
            },
            enableColumnFilter: false,
            enableSorting: true,
            className: "no-padding",
            sortingFn: (rowA, rowB, columnId) => {
                const primarySort = sortAscendingWithNullsFirst(rowA.getValue(columnId), rowB.getValue(columnId));
                if (primarySort !== 0) {
                    return primarySort;
                }

                return rowB.index - rowA.index;
            }
        };
    });

    return [...detailedSummaryColumns, averageColumn, ...dateColumns];
};
